import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../auth/useAuth';
import { useToast } from '../../ui/hooks';
import { useReceptionLocale } from '../../reception/locale';
import { useDepartmentLocale } from '../../ui/locale/departmentLocale';

import { fetchDeveloperSimulationStateResponse, stopDeveloperSimulationResponse } from '../../api/developer';

interface SimulationState {
  active: boolean;
  role?: string | null;
  target_employee_id?: number | null;
  target_student_id?: number | null;
  effects_policy?: string | null;
}

const SimulationBanner: React.FC = () => {
  const { user, refreshAuth } = useAuth();
  const { push } = useToast();
  const fallback = useReceptionLocale();
  const locale = useDepartmentLocale();
  const t = locale?.t ?? fallback.t;
  const format = locale?.format ?? fallback.format;
  const [busy, setBusy] = useState(false);
  const [fallbackSimulation, setFallbackSimulation] = useState<SimulationState | null>(null);
  const isDeveloper = Boolean(user?.is_developer || String(user?.role || '').toLowerCase() === 'developer');

  const simulation = user?.simulation?.active ? user.simulation : fallbackSimulation;
  const active = Boolean(simulation?.active);

  const refreshSimulationState = useCallback(async () => {
    if (!isDeveloper) {
      setFallbackSimulation(null);
      return;
    }
    try {
      const res = await fetchDeveloperSimulationStateResponse();
      if (!res.ok) {
        setFallbackSimulation(null);
        return;
      }
      const data = await res.json();
      setFallbackSimulation({
        active: Boolean(data?.active),
        role: data?.role ?? null,
        target_employee_id: data?.target_employee_id ?? null,
        target_student_id: data?.target_student_id ?? null,
        effects_policy: data?.effects_policy ?? null,
      });
    } catch {
      setFallbackSimulation(null);
    }
  }, [isDeveloper]);

  useEffect(() => {
    if (!isDeveloper) return;
    void refreshSimulationState();
  }, [isDeveloper, refreshSimulationState]);

  useEffect(() => {
    if (!isDeveloper) return;
    const onFocus = () => { void refreshSimulationState(); };
    window.addEventListener('focus', onFocus);
    window.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('visibilitychange', onFocus);
    };
  }, [isDeveloper, refreshSimulationState]);

  const label = useMemo(() => {
    if (!active) return '';
    const role = t((simulation?.role || 'unknown').replaceAll('_', ' '));
    const targetEmployee = simulation?.target_employee_id ? format('employee #{0}', [simulation.target_employee_id]) : null;
    const targetStudent = simulation?.target_student_id ? format('student #{0}', [simulation.target_student_id]) : null;
    const target = targetEmployee || targetStudent || t('none');
    return format('Simulation active: {0} (target {1})', [role, target]);
  }, [active, simulation?.role, simulation?.target_employee_id, simulation?.target_student_id, t, format]);

  const stopSimulation = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await stopDeveloperSimulationResponse();
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.detail || 'Failed to stop simulation');
      }
      await refreshSimulationState();
      await refreshAuth({ force: true });
      push?.(t('Simulation stopped'), 'success');
    } catch (err) {
      push?.(err instanceof Error ? err.message : t('Failed to stop simulation'), 'danger');
    } finally {
      setBusy(false);
    }
  }, [busy, push, refreshAuth, refreshSimulationState]);

  if (!isDeveloper || !active) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 12,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 1200,
        background: '#fff4e5',
        border: '1px solid #f0ba70',
        color: '#7a4a11',
        borderRadius: 999,
        padding: '7px 14px',
        display: 'flex',
        flexWrap: 'wrap',
        width: 'max-content',
        maxWidth: 'calc(100vw - 24px)',
        overflowWrap: 'anywhere',
        alignItems: 'center',
        gap: 10,
        boxShadow: '0 8px 24px rgba(91, 53, 9, 0.16)',
      }}
    >
      <strong style={{ fontSize: 12 }}>{t('SIMULATION')}</strong>
      <span style={{ fontSize: 12 }}>{label}</span>
      <button
        type="button"
        onClick={() => { void stopSimulation(); }}
        disabled={busy}
        style={{
          border: '1px solid #d19038',
          background: '#fff',
          color: '#7a4a11',
          borderRadius: 999,
          padding: '4px 10px',
          fontSize: 12,
          fontWeight: 600,
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.7 : 1,
        }}
      >
        {t(busy ? 'Stopping...' : 'Stop')}
      </button>
      <Link
        to="/developer/cockpit"
        style={{
          color: '#7a4a11',
          fontSize: 12,
          fontWeight: 600,
          textDecoration: 'underline',
        }}
      >
        {t('Cockpit')}
      </Link>
    </div>
  );
};

export default SimulationBanner;


